import React from "react";
import { FaPlay, FaStream } from "react-icons/fa";
import Button from "@material-ui/core/Button";
import { Link } from "react-router-dom";

import "../../App.css";
import "./Home.css";

export const Homepage = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="home-hero">
        <div className="home-hero-content">
          <div className="home-hero-text">
            <h1>Stop Procrastinating. Start Doing.</h1>
            <h1 className="highlighted-title">Eliminate Decision Fatigue & Boost Focus</h1>
            <p className="home-subtitle">
              Perfect for students, freelancers, and professionals who struggle with task paralysis. 
              Rundoit helps you choose what to do next, protect your focus, and keep your task list moving.
              Start with the free tier, save your tasks, and build a workflow that fits your day.
            </p>
            <div className="hero-buttons">
              <Button 
                variant="contained" 
                color="primary" 
                size="large" 
                className="cta-button"
              >
                <Link
                  to="/signup"
                  style={{ textDecoration: "none", color: "white" }}
                >
                  Start Free
                </Link>
              </Button>
              <Button 
                variant="outlined" 
                size="large" 
                className="signin-button"
              >
                <Link
                  to="/login"
                  style={{ textDecoration: "none", color: "white" }}
                >
                  Sign In
                </Link>
              </Button>
              <Button
                variant="outlined"
                size="large"
                className="signin-button"
              >
                <Link
                  to="/demo"
                  style={{ textDecoration: "none", color: "white" }}
                >
                  Try Demo
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2>Three Focus Modes for Different Kinds of Days</h2>
        <p className="section-subtitle">
          Sign in to save your tasks, choose a mode, and let Rundoit turn your list into a clear next action.
        </p>
        <div className="features-grid">
          {/* Random Mode */}
          <div className="feature-card">
            <div className="feature-icon">
              <span style={{fontSize: '48px'}}>🎲</span>
            </div>
            <h3>Random Mode</h3>
            <p className="mode-description">
              <strong>Perfect for:</strong> Choosing one meaningful task for the day
            </p>
            <p>
              Rundoit picks one main task and keeps it highlighted for the day, so you can stop reshuffling 
              your list and commit to finishing one important thing.
            </p>
            <div className="use-case">
              <strong>Free tier:</strong> Save up to 5 tasks and let Random Mode choose today's focus.
            </div>
          </div>

          {/* Start Mode */}
          <div className="feature-card">
            <div className="feature-icon">
              <FaPlay size={48} color="#11998e" />
            </div>
            <h3>Start Mode</h3>
            <p className="mode-description">
              <strong>Perfect for:</strong> Working through tasks in order
            </p>
            <p>
              Move sequentially through your list with 25-minute focus sessions and built-in breaks, 
              giving your day a simple Pomodoro rhythm.
            </p>
            <div className="use-case">
              <strong>Best for:</strong> Admin days, study blocks, client work, and lists that already have an order.
            </div>
          </div>

          {/* Flow Mode */}
          <div className="feature-card">
            <div className="feature-icon">
              <FaStream size={48} color="#f46b45" />
            </div>
            <h3>Flow Mode</h3>
            <p className="mode-description">
              <strong>Perfect for:</strong> Deep work with flexible breaks
            </p>
            <p>
              Start an open-ended focus timer, stay with the selected task, and pause whenever you need a break. 
              Flow Mode is built for work sessions that should not feel boxed in.
            </p>
            <div className="use-case">
              <strong>Best for:</strong> Writing, coding, research, design, and other work that benefits from momentum.
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="how-it-works-container">
          <h2>How Each Mode Works</h2>
          <p className="section-subtitle">
            Create a free account, add your tasks, then choose the mode that matches your energy.
          </p>
          <div className="workflow-grid">
            <div className="workflow-card">
              <div className="workflow-icon random-workflow">
                <span style={{fontSize: '32px', color: 'white'}}>🎲</span>
              </div>
              <h3>Random Mode</h3>
              <div className="workflow-steps">
                <div className="step">1. Add up to 5 tasks on the free tier</div>
                <div className="step">2. Click Random</div>
                <div className="step">3. Get one main task for today</div>
                <div className="step">4. Finish it before choosing more</div>
              </div>
            </div>
            
            <div className="workflow-card">
              <div className="workflow-icon start-workflow">
                <FaPlay size={32} color="white" />
              </div>
              <h3>Start Mode</h3>
              <div className="workflow-steps">
                <div className="step">1. Put your tasks in order</div>
                <div className="step">2. Click Start</div>
                <div className="step">3. Work for 25 minutes</div>
                <div className="step">4. Take a break, then continue</div>
              </div>
            </div>
            
            <div className="workflow-card">
              <div className="workflow-icon flow-workflow">
                <FaStream size={32} color="white" />
              </div>
              <h3>Flow Mode</h3>
              <div className="workflow-steps">
                <div className="step">1. Choose Flow</div>
                <div className="step">2. Start the open-ended timer</div>
                <div className="step">3. Stay with the highlighted task</div>
                <div className="step">4. Pause for breaks anytime</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta-section">
        <div className="home-cta-container">
          <h2>Ready to Eliminate Decision Fatigue?</h2>
          <p>
            Use the free tier to save your first tasks and build a daily focus habit. 
            Already have an account? Sign in and get back to your list.
          </p>
          <div className="cta-buttons">
            <Button 
              variant="contained" 
              color="primary" 
              size="large" 
              className="cta-button"
            >
              <Link
                to="/signup"
                style={{ textDecoration: "none", color: "white" }}
              >
                Start Free
              </Link>
            </Button>
            <Button 
              variant="outlined" 
              size="large" 
              className="signin-button cta-signin-button"
            >
              <Link
                to="/login"
                style={{ textDecoration: "none", color: "white" }}
              >
                Sign In
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
